import { useState, useEffect, useMemo, useRef } from 'react';
import { Upload, AlertCircle, CheckCircle, CheckCircle2, XCircle, Info, Database, Calendar, Clock, Send, X, ChevronDown, ArrowRight, Copy, Check, Activity, Lock } from 'lucide-react';
import { Project } from '../types/project';
import { readFile } from '../lib/ami/readFile';
import { browserReadDeps } from '../lib/ami/layoutLibrary';
import { summarizeAmiSeries } from '../lib/amiSummary';
import { PEAK_CEILING_KW, PEAK_CEILING_WARNING_PREFIX } from '../lib/ami/parser';
import { logParserEvent, updateParserAction } from '../lib/ami/parserTelemetry';
import { SUSPECT_PEAK_MESSAGE, type PlausibilityFlag } from '../lib/ami/plausibilityValidator';
import { buildAuditPayload, type AuditArtifacts } from '../lib/ami/auditPayload';
import { analyzeDataQuality, generateDataQualityNotes, DataQualityResult } from '../lib/dataQuality';
import { mergeAMIDatasets, shouldRefuseMerge, type ConflictStrategy } from '../lib/amiMerge';
import {
  detectOutlierReview,
  describeOutlierReview,
  buildOutlierDecisionNote,
  type OutlierReview,
} from '../lib/ami/outliers';
import { supabase } from '../lib/supabase';
import { GetDataPage } from './GetDataPage';
import { Tooltip } from './Tooltip';
import { useUtilityDetail } from '../hooks/useUtility';
import { isUploadRoute, type SentRoute } from '../lib/utilityRoutes';
import { expectedByDate, formatExpectedBy, isUsableTemplate } from '../lib/requestForm';
import { ColumnPicker } from './ColumnPicker';
import { columnLetter } from '../lib/ami/pickerFormat';
import type { ColumnChoices, ColumnOverride, ManualPickerRequired } from '../lib/ami/types';
import { citationsFor } from '../lib/calc/citations';
import { DemandYearChart, PeakDayChart, DemandMonthTable } from './DemandChart';
import { buildDemandSeries, formatLongDate, formatMonthDay, formatMonthDayYear } from '../lib/demandSeries';
import { detectSuspectWindows, regroupEstimatedRuns, serviceDeliverableKw, type TimeRange } from '../lib/ami/suspectPeaks';
import { activeExclusions, readingsInEffect } from '../lib/peakExclusions';
import { SuspectPeakPanel } from './SuspectPeakPanel';
import { useAuth } from '../contexts/AuthContext';

/**
 * The columns a file turned out to have, for a refusal message. Read from the
 * parser's detected header row so it reflects the row it actually treated as the
 * header, blanks named by position.
 */
const PICK_BELOW = 'Choose the date/time and demand columns below.';
const EMAIL_SUPPORT = 'Please email the file to support@trueloadapp.com and we will add support for this layout.';

function describeColumns(metadata: unknown, pickable: boolean): string {
  const header = (metadata as { fingerprintHeader?: (string | null)[] | null } | undefined)?.fingerprintHeader ?? [];
  const names = header.map((h, i) => (h ?? '').trim() || `column ${i + 1}`);
  const listed = names.length > 0 ? `Columns in this file: ${names.join(', ')}. ` : '';
  return `${listed}${pickable ? PICK_BELOW : EMAIL_SUPPORT}`;
}

/** A file whose columns detection could not settle, held until the person picks them. */
interface PendingPick {
  file: File;
  picker: ManualPickerRequired;
}

/**
 * The last single-file upload that loaded, kept so its columns can be corrected
 * without uploading again. Holds the File (re-read for the second parse), the
 * parser's choices, and the series the file was merged into, so a correction
 * re-merges into exactly that and never into its own wrong readings.
 */
interface LastUpload {
  file: File;
  choices: ColumnChoices;
  base: Array<{ timestamp: string; kw: number }>;
}

/** "B: interval_ending_time" for the column a parse used in a role. */
function describeChoice(choices: ColumnChoices, role: 'timestamp' | 'demand'): string {
  const index = role === 'timestamp' ? choices.chosen.timestampIndex : choices.chosen.demandIndex;
  const header = choices.columns.find(c => c.index === index)?.header?.trim();
  return `${columnLetter(index)}: ${header || `column ${index + 1}`}`;
}

/** Everything `finishUpload` needs that is settled before the outlier gate. */
interface UploadContext {
  qualityNotes: string[];
  conflictsResolved: number;
  duplicatesRemoved: number;
  anyGenerationColumn: boolean;
  maxGrossPeakKW: number | null;
  flags: PlausibilityFlag[];
  telemetryPromises: Promise<string | null>[];
  formatLabel: string;
  warnings: string[];
  /** Utility-estimated runs: the new files' plus, on Add, those already stored. */
  estimatedRuns: TimeRange[];
}

interface FileParseResult {
  name: string;
  pointCount: number;
  errors: string[];
  format: string;
}

interface ConsentRequest {
  id: string;
  /** The link's credential. Read so the contractor can send the link himself —
   *  the status line tells him to, and until now only the customer's inbox had
   *  a copy. RLS scopes this row to the project's owner, who is that person. */
  token: string;
  status: 'pending' | 'consented' | 'declined';
  customer_name: string;
  created_at: string;
  token_expires_at: string;
  consented_at: string | null;
  utility_id: string | null;
  utility_name: string;
  utility_email: string;
  /** Which job the link did: an email request or the homeowner's own upload. */
  data_route: 'email_request' | 'self_download' | 'customer_request' | 'utility_request' | null;
  upload_expires_at: string | null;
  homeowner_uploaded_at: string | null;
  upload_reading_count: number | null;
  upload_first_reading: string | null;
  upload_last_reading: string | null;
  /** Form route (the utility's own web form): set at send. */
  request_form_template: string | null;
  /** When the homeowner pressed "I submitted the form". */
  customer_submitted_at: string | null;
  /** When the homeowner first pressed the button that opens the utility's form. */
  form_opened_at: string | null;
  /** Set when the utility sends the file to TrueLoad's receiving address. */
  receiving_code: string | null;
  /** When the receiving address stops accepting mail. */
  receiving_expires_at: string | null;
  /** When the utility's file, emailed to the receiving address, loaded. */
  utility_delivered_at: string | null;
}

interface AMIDataInputProps {
  projectId: string | null;
  project: Partial<Project>;
  amiDataCount: number;
  amiData: Array<{ timestamp: string; kw: number }>;
  onChange: (updates: Partial<Project>) => void;
  onUploadCSV: (data: Array<{ timestamp: string; kw: number }>) => void;
  /** May be async — the caller awaits it before touching AMI storage again, so
   *  a Replace cannot write its new series while the old one is still clearing. */
  onDeleteData: () => void | Promise<void>;
  isReadOnly?: boolean;
  showRequiredHighlights?: boolean;
  subscriptionStatus?: 'active' | 'canceled' | 'past_due' | null;
  /** The paywall as Results sees it. The peak's value, the chart's scale and the
   *  peak-day detail render only when true — the measured peak is paid content. */
  unlocked?: boolean;
  onRelockTriggered?: () => void;
  /**
   * Re-read the project and its readings from the cloud. Used when a homeowner
   * upload lands on a project the contractor has open: the readings live on the
   * server, not in this browser, until they are fetched.
   */
  onReloadProject?: () => void | Promise<void>;
  /** Jumps to another tab. Drives the "Continue to Results" payoff after upload. */
  onNavigate?: (tab: 'project' | 'service' | 'equipment' | 'ami' | 'results' | 'help') => void;
}

export function AMIDataInput({
  projectId,
  project,
  amiDataCount,
  amiData,
  onChange,
  onUploadCSV,
  onDeleteData,
  isReadOnly = false,
  showRequiredHighlights = false,
  subscriptionStatus = null,
  unlocked = false,
  onRelockTriggered,
  onReloadProject,
  onNavigate,
}: AMIDataInputProps) {
  // The measured method's section number tracks the project's code edition:
  // 220.87 under 2020/2023, 120.87 where the 2026 Article 120 reorg applies.
  const cite = citationsFor(project).measured;
  const { user: authUser, userProfile } = useAuth();
  const [uploadErrors, setUploadErrors] = useState<string[]>([]);
  const [uploadWarnings, setUploadWarnings] = useState<string[]>([]);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [detectedFormat, setDetectedFormat] = useState<string>('');
  const [isDragging, setIsDragging] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteApiError, setDeleteApiError] = useState<string | null>(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [conflictStrategy, setConflictStrategy] = useState<ConflictStrategy>('maximum');
  const [fileResults, setFileResults] = useState<FileParseResult[]>([]);

  // Add-vs-Replace choice for a second upload onto a project that already has AMI
  // data. showAddReplaceChoice gates the modal; pendingFiles holds the files
  // awaiting the choice. replaceUploadFilesRef carries the files across the
  // (possibly async) clear step so handleDeleteData can parse them once the old
  // series has been cleared — a ref, not state, so async delete/replace handlers
  // read the current value rather than a stale closure.
  const [showAddReplaceChoice, setShowAddReplaceChoice] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const replaceUploadFilesRef = useRef<File[] | null>(null);

  // Files detection could not read, waiting for the person to name the two
  // columns. The File object is kept, not its bytes: it is re-read for the
  // second parse, which runs with the chosen columns forced.
  const [pendingPicks, setPendingPicks] = useState<PendingPick[]>([]);
  const [pickBusy, setPickBusy] = useState(false);
  // The upload a person may still correct, and the correction panel when open.
  const [lastUpload, setLastUpload] = useState<LastUpload | null>(null);
  const [repick, setRepick] = useState<LastUpload | null>(null);

  // Layer 2 plausibility verification banner — state is per-parse (reset on every upload).
  const [plausibilityFlags, setPlausibilityFlags] = useState<PlausibilityFlag[]>([]);
  const [plausibilityRowIds, setPlausibilityRowIds] = useState<string[]>([]);
  const [plausibilityAction, setPlausibilityAction] = useState<'pending' | 'confirmed' | 'disputed' | null>(null);
  // True when the banner was rehydrated from a persisted pending telemetry row
  // (as opposed to a fresh in-session parse). Lets the banner render without an
  // uploadSuccess in the current session.
  const [plausibilityRehydrated, setPlausibilityRehydrated] = useState(false);

  // A parsed upload held back for a preparer decision about readings that stand
  // far apart from the rest of the file. Nothing is written to the project until
  // one of the three choices is taken.
  const [pendingOutlier, setPendingOutlier] = useState<{
    review: OutlierReview;
    merged: Array<{ timestamp: string; kw: number }>;
    context: UploadContext;
  } | null>(null);
  // A fresh in-session parse always wins over rehydration — even if the async
  // rehydration query resolves after the parse has already populated the banner.
  const freshParseRef = useRef(false);
  // Layout library bookkeeping for the current upload: proposals to save once
  // the preparer confirms a reading only Claude's instructions could make, and
  // the files read from saved instructions, so a correction retires them.
  const pendingLayoutSavesRef = useRef<string[]>([]);
  const libraryReadsRef = useRef<Map<string, string>>(new Map());

  const [consentRequest, setConsentRequest] = useState<ConsentRequest | null>(null);
  const [consentLoading, setConsentLoading] = useState(false);
  // The Get data page: every route and the send form live there, not on the tab.
  const [showGetData, setShowGetData] = useState(false);
  const [requestSuccess, setRequestSuccess] = useState<{ expiresAt: string; dataRoute: SentRoute } | null>(null);

  const [qualityResult, setQualityResult] = useState<DataQualityResult | null>(null);

  const [showFormatsModal, setShowFormatsModal] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showManualEntry, setShowManualEntry] = useState(false);
  // Once a file is attached, uploading again is the fringe case: the dropzone
  // folds away behind this and the step reads as finished instead of as a
  // still-empty form.
  const [showReplace, setShowReplace] = useState(false);
  // The done card's one control for replacing or deleting, and the checklist
  // behind the quality badge.
  const [showManageMenu, setShowManageMenu] = useState(false);
  const [showChecklist, setShowChecklist] = useState(false);
  const manageMenuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!showManageMenu) return;
    const onDown = (e: MouseEvent) => {
      if (manageMenuRef.current && !manageMenuRef.current.contains(e.target as Node)) setShowManageMenu(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [showManageMenu]);
  const [copiedLink, setCopiedLink] = useState(false);

  // The chosen utility's row decides what the Get data page shows. "Other (not
  // listed)" is a real choice with its own generic route; a chosen utility
  // whose row has not arrived yet is not, so the button waits. A utility with
  // no route still opens the page: it explains, and takes the demand capture.
  const { utility: utilityRow, loading: utilityLoading } = useUtilityDetail(project.utility_id);
  const utilityName = project.ami_data_provider || '';
  const utilityKnown = project.utility_id
    ? !utilityLoading && utilityRow != null
    : utilityName.trim().length > 0;

  useEffect(() => {
    if (amiData.length > 0) {
      const result = analyzeDataQuality(amiData, project);
      setQualityResult(result);
      const analysisNotes = generateDataQualityNotes(result.issues);

      const existingNotes = project.ami_data_quality_notes || '';
      const hasParserNotes = existingNotes.includes('PARSING NOTES:');

      if (hasParserNotes) {
        const parserSection = existingNotes.split('\n\n')[0];
        onChange({ ami_data_quality_notes: `${parserSection}\n\n${analysisNotes}` });
      } else {
        onChange({ ami_data_quality_notes: analysisNotes });
      }
    } else {
      setQualityResult(null);
    }
  }, [amiData.length]);

  // Fetch consent request status for this project
  useEffect(() => {
    if (!projectId || projectId.startsWith('local_')) return;
    fetchConsentStatus();
  }, [projectId]);

  // Realtime: refresh consent status when the record changes for this project
  useEffect(() => {
    if (!projectId || projectId.startsWith('local_')) return;

    const channel = supabase
      .channel(`consent-status-${projectId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'ami_consent_requests',
          filter: `project_id=eq.${projectId}`,
        },
        () => { fetchConsentStatus(); }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [projectId]);

  // Rehydrate the verification banner for a reopened project. The banner
  // otherwise lives only in component state, so a pending verification is lost
  // on remount/navigation/reload — stranding the telemetry row at
  // user_action='pending' and never re-prompting the contractor. On mount (for
  // a persisted project that has attached AMI data) we pull the most recent
  // pending, flagged parser_events row and render the banner from its flags,
  // wired to the same Confirm/Dispute handlers via that row's id.
  // Precedence: a fresh in-session parse always wins (freshParseRef guard);
  // delete/replace clears the banner; query errors are silent (console.warn).
  const hasAttachedAmi = amiDataCount > 0;
  useEffect(() => {
    if (isReadOnly) return;
    // `demo_` ids are not UUIDs, so this query errors out in Postgres before it
    // can find anything. Same class as the local_ guard beside it.
    if (!projectId || projectId.startsWith('local_') || projectId.startsWith('demo_')) return;
    if (!hasAttachedAmi) return;
    if (freshParseRef.current) return; // a fresh parse already owns the banner

    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase
          .from('parser_events')
          .select('id, flags')
          .eq('project_id', projectId)
          .eq('user_action', 'pending')
          .gt('flag_count', 0)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (cancelled || freshParseRef.current) return;
        if (error) {
          console.warn('[plausibility] rehydration query failed:', error.message);
          return;
        }
        const flags = (data?.flags ?? []) as PlausibilityFlag[];
        if (!data || flags.length === 0) return;

        setPlausibilityFlags(flags);
        setPlausibilityRowIds([data.id as string]);
        setPlausibilityAction('pending');
        setPlausibilityRehydrated(true);
      } catch (err) {
        if (!cancelled) {
          console.warn('[plausibility] rehydration threw:', err instanceof Error ? err.message : err);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [projectId, hasAttachedAmi, isReadOnly]);

  async function fetchConsentStatus() {
    if (!projectId || projectId.startsWith('local_')) return;
    setConsentLoading(true);
    try {
      const { data, error } = await supabase
        .from('ami_consent_requests')
        .select('id, token, status, customer_name, created_at, token_expires_at, consented_at, utility_id, utility_name, utility_email, data_route, upload_expires_at, homeowner_uploaded_at, upload_reading_count, upload_first_reading, upload_last_reading, request_form_template, customer_submitted_at, form_opened_at, receiving_code, receiving_expires_at, utility_delivered_at')
        .eq('project_id', projectId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (!error && data) {
        setConsentRequest(data as ConsentRequest);
      }
    } catch (err) {
      console.error('Error fetching consent status:', err);
    } finally {
      setConsentLoading(false);
    }
  }

  // A homeowner upload that lands while this project is open: the readings
  // are on the server and this browser holds none, so fetch them once.
  //
  // "An upload is on record and this browser holds no readings" does not prove
  // the readings are on the server: the consent row keeps homeowner_uploaded_at
  // after the contractor deletes them. So count the rows first. Null means not
  // known, and the Load into project button stays offered.
  const [serverReadingCount, setServerReadingCount] = useState<number | null>(null);
  const autoLoadedUploadRef = useRef<string | null>(null);
  useEffect(() => {
    // The utility's file, loaded from TrueLoad's receiving address, lands the same way.
    const uploadedAt = consentRequest?.homeowner_uploaded_at ?? consentRequest?.utility_delivered_at ?? null;
    if (!uploadedAt || !projectId) return;
    if (amiDataCount > 0) {
      // This upload is already here. If the count drops to zero later, that is
      // the contractor deleting it, not a new arrival to fetch.
      autoLoadedUploadRef.current = uploadedAt;
      return;
    }

    let cancelled = false;
    void (async () => {
      const { count, error } = await supabase
        .from('ami_data_points')
        .select('*', { count: 'exact', head: true })
        .eq('project_id', projectId);
      if (cancelled || error) return;
      setServerReadingCount(count ?? 0);
      if (!count || !onReloadProject || isReadOnly) return;
      if (autoLoadedUploadRef.current === uploadedAt) return;
      autoLoadedUploadRef.current = uploadedAt;
      void onReloadProject();
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, consentRequest?.homeowner_uploaded_at, consentRequest?.utility_delivered_at, amiDataCount]);

  function handleRequestSuccess(expiresAt: string, dataRoute: SentRoute) {
    setShowGetData(false);
    setRequestSuccess({ expiresAt, dataRoute });
    fetchConsentStatus();
  }

  // Gatekeeper for BOTH the file picker (handleFileUpload) and drag-and-drop
  // (handleDrop). If the project already holds AMI data, ask Add vs Replace before
  // parsing anything. If it is empty, behave exactly as before (straight to parse).
  const processFiles = async (files: File[]) => {
    if (amiData.length > 0) {
      setPendingFiles(files);
      setShowAddReplaceChoice(true);
      return;
    }
    await runProcessFiles(files, false);
  };

  // The actual parse + merge. replaceExisting=true means "ignore the current
  // series entirely and load only these files" — used by the Replace choice,
  // where the old series is cleared first. Passing an empty existing set here
  // (rather than reading amiData) is deliberate: after the clear, the amiData
  // prop can still be stale within the same tick, so we must not merge into it.
  // `base`, when given, is the series to merge into instead of the project's
  // current one. A column correction passes the series its file was originally
  // merged into: merging into the current series would keep the wrong readings
  // the correction exists to remove, and replacing outright would drop any
  // earlier file the person had added.
  const runProcessFiles = async (
    files: File[],
    replaceExisting: boolean,
    columnOverride?: ColumnOverride,
    base?: Array<{ timestamp: string; kw: number }>
  ) => {
    setUploadErrors([]);
    setRepick(null);
    setLastUpload(null);
    setUploadWarnings([]);
    setUploadSuccess(false);
    setDetectedFormat('');
    setFileResults([]);
    // Reset the plausibility banner for this new parse. A fresh in-session parse
    // takes precedence over (and supersedes) any rehydrated banner.
    freshParseRef.current = true;
    setPlausibilityRehydrated(false);
    setPlausibilityFlags([]);
    setPlausibilityRowIds([]);
    setPlausibilityAction(null);

    pendingLayoutSavesRef.current = [];
    libraryReadsRef.current = new Map();

    const parsedDatasets: Array<Array<{ timestamp: string; kw: number }>> = [];
    const newFileResults: FileParseResult[] = [];
    const allQualityNotes: string[] = [];
    const allErrors: string[] = [];
    const allWarnings: string[] = [];
    // Layer 2: aggregate plausibility flags + telemetry row ids across all files in this upload.
    const aggregatedFlags: PlausibilityFlag[] = [];
    const aggregatedEstimated: TimeRange[] = [];
    const pendingTelemetryPromises: Promise<string | null>[] = [];
    const multi = files.length > 1;
    let anyLowConfidence = false;
    let anyGenerationColumn = false;
    let maxGrossPeakKW: number | null = null;
    const picks: PendingPick[] = [];
    let singleChoices: ColumnChoices | null = null;

    for (const file of files) {
      const extension = file.name.toLowerCase().split('.').pop();
      try {
        // Always hand the parser bytes. file.text() decodes as UTF-8 no matter
        // what the file is, which silently mangled UTF-16 utility exports; the
        // parser sniffs the encoding instead.
        void extension;
        const fileContent: string | ArrayBuffer = await file.arrayBuffer();

        // A person's column choice applies to the one file it was made for.
        // readFile wraps the parser with the layout library and, for a layout
        // nobody has seen, Claude's proposal (src/lib/ami/readFile.ts).
        const outcome = await readFile(
          fileContent,
          file.name,
          {
            ...(columnOverride && files.length === 1 ? { columnOverride } : {}),
            utility: project.ami_data_provider ?? null,
          },
          browserReadDeps
        );
        const result = outcome.result;
        if (outcome.pendingSave) pendingLayoutSavesRef.current.push(outcome.pendingSave.proposalId);
        if (outcome.layoutKey && result.metadata?.instructionsSource === 'library') {
          libraryReadsRef.current.set(file.name, outcome.layoutKey);
        }
        const { data, warnings, detectedFormat: format, metadata, columnSelectionLowConfidence } = result;
        // When the parser could not tell which columns to use, add what it saw
        // and, where a person could settle it, hold the file for the picker.
        // The customer and support both need the column names, not a verdict:
        // the first Burlington Electric upload (2026-09-08) reached this screen
        // as "No valid data found in any uploaded file" and nobody could tell why.
        const picker = (result as { manualPickerRequired?: ManualPickerRequired }).manualPickerRequired;
        const pickable = !!picker && picker.candidates.timestamp.length > 0 && picker.candidates.demand.length > 0;
        const errors = picker ? [...result.errors, describeColumns(metadata, pickable)] : result.errors;
        if (picker && pickable) picks.push({ file, picker });

        // ── Layer 2 telemetry (strictly fire-and-forget; never awaited here) ──
        const fileFlags = (metadata?.plausibilityFlags ?? []) as PlausibilityFlag[];
        const tsum = (metadata?.telemetry ?? {}) as {
          sheet_count?: number | null; chosen_sheet?: string | null; header_row_index?: number | null;
          header_confidence?: number | null; timestamp_column?: string | null; demand_column?: string | null;
          demand_unit?: string | null; interval_minutes?: number | null; row_count?: number | null;
          expected_row_count?: number | null; span_days?: number | null; peak_kw?: number | null;
        };
        // Post-dedup row count: the parser's telemetry row_count is the raw
        // pre-merge extractor length, but mergeAMIDatasets (below) collapses any
        // duplicate-timestamp rows, so Parser Health would otherwise show more
        // rows than actually loaded. Count distinct timestamps here — the same
        // instant key the merge dedups on — so telemetry reflects the loaded
        // total without re-running the merge's conflict-resolution logic.
        const loadedRowCount = data.length > 0
          ? new Set(data.map(p => p.timestamp)).size
          : (tsum.row_count ?? null);
        // Observational Layer-3 fields live on metadata but aren't in its strict
        // type — read them through a loose view.
        const parseMeta = metadata as Record<string, unknown> | undefined;
        if (!multi && data.length > 0) {
          singleChoices = (parseMeta?.columnChoices as ColumnChoices | undefined) ?? null;
        }
        const parseStatus: 'success' | 'manual_required' | 'error' =
          (result as { manualPickerRequired?: unknown }).manualPickerRequired ? 'manual_required'
            : (data.length === 0 && errors.length > 0) ? 'error'
            : 'success';
        const telemetryPromise = logParserEvent({
          project_id: projectId,
          utility_name: project.ami_data_provider ?? null,
          file_name: file.name,
          file_size_bytes: file.size,
          file_type: extension ?? null,
          sheet_count: tsum.sheet_count ?? null,
          chosen_sheet: tsum.chosen_sheet ?? null,
          header_row_index: tsum.header_row_index ?? null,
          header_confidence: tsum.header_confidence ?? null,
          timestamp_column: tsum.timestamp_column ?? null,
          demand_column: tsum.demand_column ?? null,
          demand_unit: tsum.demand_unit ?? null,
          interval_minutes: tsum.interval_minutes ?? null,
          row_count: loadedRowCount,
          expected_row_count: tsum.expected_row_count ?? null,
          span_days: tsum.span_days ?? null,
          peak_kw: tsum.peak_kw ?? null,
          parse_status: parseStatus,
          flags: fileFlags,
          fingerprintHeader: (parseMeta?.fingerprintHeader as (string | null)[] | null | undefined) ?? null,
          fingerprintColumnKinds: (parseMeta?.fingerprintColumnKinds as string[] | null | undefined) ?? null,
        });
        if (fileFlags.some(f => f.severity === 'verify')) {
          pendingTelemetryPromises.push(telemetryPromise);
        }
        // A refused upload, or one that needs the preparer to pick columns,
        // emails Jake. Fire-and-forget; the function re-reads the row itself.
        if (parseStatus !== 'success') {
          void telemetryPromise.then(rowId => {
            if (!rowId) return;
            void fetch('/.netlify/functions/parser-failure-alert', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ parserEventId: rowId, reason: errors.join('\n') }),
            }).catch(err => console.warn('[parserAlert] invocation failed:', err));
          });
        }

        // ── Layer 3 AI audit (SHADOW MODE) — fire-and-forget after the telemetry
        //    insert resolves. The client always invokes; the server-side kill
        //    switch decides whether to audit. Never blocks or delays the parse.
        const auditArtifacts = parseMeta?.auditArtifacts as AuditArtifacts | undefined;
        if (auditArtifacts) {
          void telemetryPromise.then(rowId => {
            if (!rowId) return;
            try {
              const auditPayload = buildAuditPayload(auditArtifacts, project.ami_data_provider ?? null);
              void fetch('/.netlify/functions/parser-audit-background', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ parserEventId: rowId, payload: auditPayload }),
              }).catch(err => console.warn('[parserAudit] invocation failed:', err));
            } catch (err) {
              console.warn('[parserAudit] payload build failed:', err);
            }
          });
        }
        for (const f of fileFlags) {
          aggregatedFlags.push(multi ? { ...f, message: `${file.name}: ${f.message}` } : f);
        }
        aggregatedEstimated.push(...(metadata?.estimatedRuns ?? []));

        if (columnSelectionLowConfidence) anyLowConfidence = true;
        if (metadata?.generationColumnDetected) anyGenerationColumn = true;
        if (metadata?.grossPeakKW != null) {
          maxGrossPeakKW = maxGrossPeakKW === null
            ? metadata.grossPeakKW
            : Math.max(maxGrossPeakKW, metadata.grossPeakKW);
        }
        newFileResults.push({ name: file.name, pointCount: data.length, errors, format });

        if (data.length > 0) {
          parsedDatasets.push(data);
        }

        const label = multi ? `${file.name}: ` : '';
        allErrors.push(...errors.map(e => `${label}${e}`));
        allWarnings.push(...warnings.map(w => `${label}${w}`));

        if (metadata?.dataQuality && metadata.dataQuality.length > 0) {
          const noteLabel = multi ? `[${file.name}] ` : '';
          allQualityNotes.push(...metadata.dataQuality.map(n => `${noteLabel}${n}`));
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Unknown error';
        newFileResults.push({ name: file.name, pointCount: 0, errors: [msg], format: 'Error' });
        allErrors.push(multi ? `${file.name}: ${msg}` : msg);
        // Telemetry for hard errors, with whatever fields are available (fire-and-forget).
        logParserEvent({
          project_id: projectId,
          utility_name: project.ami_data_provider ?? null,
          file_name: file.name,
          file_size_bytes: file.size,
          file_type: extension ?? null,
          sheet_count: null, chosen_sheet: null, header_row_index: null, header_confidence: null,
          timestamp_column: null, demand_column: null, demand_unit: null, interval_minutes: null,
          row_count: null, expected_row_count: null, span_days: null, peak_kw: null,
          parse_status: 'error',
          flags: [],
        });
      }
    }

    setFileResults(newFileResults);
    setPendingPicks(picks);

    if (parsedDatasets.length === 0) {
      // Nothing loaded. Whatever the parser said about why is the message; the
      // generic line is only for a parser that said nothing at all.
      const reasons = allErrors.length > 0 ? allErrors : allWarnings;
      setUploadErrors(reasons.length > 0 ? reasons : ['No valid data found in any uploaded file']);
      return;
    }

    const existingSimple = base ?? (replaceExisting
      ? []
      : amiData.map(p => ({ timestamp: p.timestamp, kw: p.kw })));
    const { data: merged, stats } = mergeAMIDatasets(
      [existingSimple, ...parsedDatasets],
      conflictStrategy
    );


    // Refuse a merge that rewrites too much of the record. Nothing is applied:
    // this returns before onChange/onUploadCSV, so the project keeps the data it
    // already had. Resolving tens of thousands of points behind a green banner is
    // what let a two-meter export read as one panel's peak.
    //
    // Add path ONLY. On Replace, handleAddReplaceReplace has already cleared the
    // existing series and incremented ami_replace_count (re-locking a non-
    // subscriber) before this runs — refusing there would strand the user with
    // their data deleted, a replacement spent, and nothing loaded. The
    // destructive step is already behind us, so the merge proceeds and the
    // conflicts are disclosed in the report instead.
    if (!replaceExisting && shouldRefuseMerge(stats)) {
      const pct = Math.round((stats.conflictsResolved / stats.totalOutputPoints) * 100);
      // Nothing is loaded, so the clash is inside what was just uploaded, and
      // there is no Replace choice to send anyone to.
      if (existingSimple.length === 0) {
        setUploadErrors([
          `${files.length > 1 ? 'These files give' : 'This file gives'} two different values for the same time at ` +
          `${stats.conflictsResolved.toLocaleString()} of ${stats.totalOutputPoints.toLocaleString()} readings (${pct}%), ` +
          'so TrueLoad cannot tell which is right. This usually means more than one meter or channel in the same ' +
          'export. Nothing was loaded. Export one meter at a time and upload that.',
        ]);
        return;
      }
      setUploadErrors([
        `This upload conflicts with the data already on this project at ${stats.conflictsResolved.toLocaleString()} ` +
        `of ${stats.totalOutputPoints.toLocaleString()} readings (${pct}%). TrueLoad will not resolve that many ` +
        'readings automatically. This usually means the file repeats data already loaded, covers the same period ' +
        'with revised values, or belongs to a different meter. No data was changed — if this file is meant to ' +
        'supersede what is loaded, upload it again and choose Replace instead of Add.',
      ]);
      return;
    }

    if (allErrors.length > 0) setUploadErrors(allErrors);

    if (anyLowConfidence) {
      allWarnings.unshift(
        'Demand column selected with low confidence — the parser could not clearly identify the kW demand column. ' +
        'Review the Peak Demand value below and confirm it matches your expected demand.'
      );
    }

    const formatLabel = multi
      ? `${files.length} files merged → ${merged.length.toLocaleString()} points`
      : newFileResults[0]?.format ?? '';

    const context: UploadContext = {
      qualityNotes: allQualityNotes,
      conflictsResolved: stats.conflictsResolved,
      duplicatesRemoved: stats.duplicatesRemoved,
      anyGenerationColumn,
      maxGrossPeakKW,
      flags: aggregatedFlags,
      telemetryPromises: pendingTelemetryPromises,
      formatLabel,
      warnings: allWarnings,
      estimatedRuns: [
        ...aggregatedEstimated,
        ...(replaceExisting || base ? [] : project.ami_estimated_runs ?? []),
      ],
    };

    // Remember this upload so its columns can be corrected in place. Single
    // files only: a correction re-reads one file, and a batch would need a
    // choice of which.
    setLastUpload(!multi && singleChoices ? { file: files[0], choices: singleChoices, base: existingSimple } : null);

    // A cluster of readings far above the rest is not something to decide on the
    // user's behalf. The old behaviour here rejected any peak over 500 kW and told
    // the user to "verify your file format and re-upload", which could not work:
    // the file parsed correctly, re-uploading reproduces it exactly, and the data
    // was discarded either way. Show them what stands out and let them choose.
    const review = detectOutlierReview(merged);
    if (review) {
      setPendingOutlier({ review, merged, context });
      return;
    }

    finishUpload(merged, context, null);
  };

  /** Everything computed for `context` is settled before the outlier gate; this
   *  runs either immediately or after the user decides what to do about it. */
  const finishUpload = (
    merged: Array<{ timestamp: string; kw: number }>,
    context: UploadContext,
    outlierNote: string | null
  ) => {
    const updates: Partial<Project> = {};
    // The same summary the homeowner upload and the on-open reconciliation
    // compute, so the peak, interval and period agree wherever they were made.
    const summary = summarizeAmiSeries(merged);
    if (summary.intervalMinutes) updates.ami_interval_minutes = summary.intervalMinutes;
    updates.ami_measurement_period = summary.measurementPeriod;

    // The outlier decision is recorded whichever way it went — accepting a
    // suspect reading is as much a preparer's judgement as excluding one, and the
    // AHJ should see which was made.
    const peakKw = summary.peakKw;

    // The parser's peak-ceiling warning describes the file as parsed. If the
    // preparer excluded the cluster that tripped it and what remains is ordinary,
    // the warning is now false — and leaving it beside a 9.58 kW result would put
    // a self-contradicting record in front of an AHJ.
    const stale = (text: string) =>
      peakKw !== null && peakKw <= PEAK_CEILING_KW && text.includes(PEAK_CEILING_WARNING_PREFIX);

    // The parser writes "NEC 220.87" as fixed text (it is synced to the harness
    // and tested against fixtures, so it stays edition-blind); these notes
    // print on the report, so the section number is resolved here.
    const withCite = (text: string) => text.replace(/NEC 220\.87/g, `NEC ${cite}`);
    const notes = (outlierNote ? [...context.qualityNotes, outlierNote] : context.qualityNotes)
      .filter(n => !stale(n))
      .map(withCite);
    if (notes.length > 0) {
      updates.ami_data_quality_notes = 'PARSING NOTES:\n- ' + notes.join('\n- ');
    }

    const visibleWarnings = context.warnings.filter(w => !stale(w)).map(withCite);
    setUploadWarnings(visibleWarnings);

    updates.max_recorded_demand_kw = peakKw;
    updates.peak_timestamp = summary.peakTimestamp;

    // Persist generation metadata so the question card and pre-selection useEffect
    // can read them from project state after processFiles completes.
    updates.ami_generation_column_detected = context.anyGenerationColumn;
    if (context.maxGrossPeakKW !== null) updates.gross_peak_kw = context.maxGrossPeakKW;

    // Persist what the merge did. Until now this lived only in component state,
    // so the count vanished on reload and the report — the document an AHJ
    // actually reads — never recorded that any reading had been rewritten.
    updates.ami_merge_conflicts_resolved = context.conflictsResolved;
    updates.ami_merge_duplicates_removed = context.duplicatesRemoved;
    updates.ami_merge_strategy = context.conflictsResolved > 0 ? conflictStrategy : null;

    // New readings, new windows: an exclusion made against the old series does
    // not carry over. Estimated runs are regrouped over the merged readings.
    updates.ami_exclusions = null;
    const runs = regroupEstimatedRuns(merged, context.estimatedRuns, summary.intervalMinutes);
    updates.ami_estimated_runs = runs.length > 0 ? runs : null;

    onChange(updates);
    onUploadCSV(merged);
    setUploadSuccess(true);
    setDetectedFormat(context.formatLabel);

    // Layer 2: surface the verification banner for this successful parse.
    // A high_peak flag raised against the pre-exclusion data is no longer true
    // once the readings that raised it are gone; drop it rather than ask the user
    // to confirm a number that is not in their project. Judged against the flag's
    // own recorded threshold so this stays correct if that threshold is retuned.
    const liveFlags = context.flags.filter(f => {
      if (f.check !== 'high_peak' || peakKw === null) return true;
      const threshold = typeof f.detail?.threshold === 'number' ? f.detail.threshold : Infinity;
      return peakKw > threshold;
    });
    setPlausibilityFlags(liveFlags);
    const hasVerifyFlag = liveFlags.some(f => f.severity === 'verify');
    setPlausibilityAction(hasVerifyFlag ? 'pending' : null);
    if (hasVerifyFlag) {
      Promise.allSettled(context.telemetryPromises).then(results => {
        const ids = results.flatMap(r => (r.status === 'fulfilled' && r.value ? [r.value] : []));
        setPlausibilityRowIds(ids);
      });
    }
  };

  const handleOutlierExclude = () => {
    if (!pendingOutlier) return;
    const { review, merged, context } = pendingOutlier;
    setPendingOutlier(null);
    const dropped = new Set(review.excludedTimestamps);
    const kept = merged.filter(p => !dropped.has(p.timestamp));
    if (kept.length === 0) {
      setUploadErrors(['Excluding those readings would leave no data. Nothing was loaded.']);
      return;
    }
    finishUpload(kept, context, buildOutlierDecisionNote(review, 'excluded'));
  };

  const handleOutlierAccept = () => {
    if (!pendingOutlier) return;
    const { review, merged, context } = pendingOutlier;
    setPendingOutlier(null);
    finishUpload(merged, context, buildOutlierDecisionNote(review, 'accepted'));
  };

  const handleOutlierDiscard = () => {
    setPendingOutlier(null);
    setFileResults([]);
    setUploadWarnings([]);
    setUploadErrors([
      'No data was loaded. Review the file, or ask the utility for a corrected export, then upload again.',
    ]);
  };

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = Array.from(event.target.files ?? []);
    if (files.length === 0) return;
    await processFiles(files);
    event.target.value = '';
  };

  // Layer 2 verification banner actions. Both update telemetry fire-and-forget;
  // neither blocks nor changes the displayed results.
  const handleConfirmPlausibility = () => {
    setPlausibilityAction('confirmed');
    plausibilityRowIds.forEach(id => { void updateParserAction(id, 'confirmed'); });
    // A reading only Claude's instructions could make joins the library once
    // the preparer confirms it.
    for (const proposalId of pendingLayoutSavesRef.current) {
      void browserReadDeps.save(proposalId, 'ai', project.ami_data_provider ?? null).catch(() => {});
    }
    pendingLayoutSavesRef.current = [];
  };

  const handleDisputePlausibility = () => {
    setPlausibilityAction('disputed');
    plausibilityRowIds.forEach(id => { void updateParserAction(id, 'disputed'); });
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);

    const allowed = ['csv', 'txt', 'xlsx', 'xls', 'ods'];
    const files = Array.from(event.dataTransfer.files).filter(
      f => allowed.includes(f.name.split('.').pop()?.toLowerCase() ?? '')
    );

    if (files.length === 0) {
      setUploadErrors(['Please upload CSV, TXT, or Excel files']);
      return;
    }

    await processFiles(files);
  };

  const isPersisted = projectId != null && !projectId.startsWith('local_');
  const isSubscriber = subscriptionStatus === 'active';
  const isUnlocked = project.results_unlocked === true;
  const replaceCount = project.ami_replace_count ?? 0;

  const deleteMode: 'standard' | 'first-replace' | 'relock' =
    !isPersisted || !isUnlocked || isSubscriber ? 'standard'
    : replaceCount === 0 ? 'first-replace'
    : 'relock';

  // Add-vs-Replace choice handlers. "Add" merges as before. "Replace" clears the
  // existing series through the SAME delete path the Delete Data button uses:
  // for unlocked non-subscribers it routes through the existing replace/re-lock
  // confirmation modal so ami_replace_count and re-lock behave identically; for
  // everyone else it clears directly. Either way the pending files are parsed
  // (replaceExisting=true) once handleDeleteData finishes clearing.
  // The person named the two columns for the first held file. Parse it again
  // with them forced. Add-vs-Replace was already decided when the file was
  // dropped: a Replace has cleared the old series by now and a failed parse
  // loaded nothing, so merging into whatever is loaded is the same answer
  // either way. Files still held from the same batch are restored afterwards
  // because the parse resets the held list to what it finds itself.
  const handlePickConfirm = async (override: ColumnOverride) => {
    const [current, ...rest] = pendingPicks;
    if (!current) return;
    setPickBusy(true);
    try {
      await runProcessFiles([current.file], false, override);
    } finally {
      setPickBusy(false);
      setPendingPicks(prev => [...prev, ...rest]);
    }
  };

  const handlePickCancel = () => {
    setPendingPicks([]);
    setUploadErrors(errs => errs.map(e => e.replace(PICK_BELOW, EMAIL_SUPPORT)));
  };

  // A correction of a successful read. The file is parsed again with the
  // chosen columns and merged into the series it was first merged into, then
  // saved through the same atomic replace every upload uses. The delete path,
  // with its replacement count and re-lock, is never involved: correcting
  // TrueLoad's own pick is not a data replacement.
  const handleRepickConfirm = async (override: ColumnOverride) => {
    const target = repick;
    if (!target) return;
    // Correcting a file read from saved instructions means the instructions
    // were wrong for it: the layout leaves the library.
    const libraryKey = libraryReadsRef.current.get(target.file.name);
    if (libraryKey) {
      void browserReadDeps.retire(libraryKey, 'The preparer corrected the columns of a file read from saved instructions.').catch(() => {});
    }
    pendingLayoutSavesRef.current = [];
    setPickBusy(true);
    try {
      await runProcessFiles([target.file], false, override, target.base);
    } finally {
      setPickBusy(false);
    }
  };

  const handleAddReplaceAdd = async () => {
    const files = pendingFiles;
    setShowAddReplaceChoice(false);
    setPendingFiles([]);
    replaceUploadFilesRef.current = null;
    await runProcessFiles(files, false);
  };

  const handleAddReplaceReplace = () => {
    const files = pendingFiles;
    setShowAddReplaceChoice(false);
    setPendingFiles([]);
    replaceUploadFilesRef.current = files;
    if (deleteMode === 'standard') {
      void handleDeleteData();
    } else {
      setShowDeleteConfirm(true);
    }
  };

  const handleAddReplaceCancel = () => {
    setShowAddReplaceChoice(false);
    setPendingFiles([]);
    replaceUploadFilesRef.current = null;
  };

  async function startSubscriptionCheckout() {
    setSubscriptionLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');
      const res = await fetch('/.netlify/functions/create-checkout-session', {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: 'subscription' }),
      });
      const data = await res.json();
      if (!res.ok || !data.url) throw new Error(data.error || 'Failed to start checkout');
      window.location.href = data.url;
    } catch (err) {
      setDeleteApiError(err instanceof Error ? err.message : 'Something went wrong');
      setSubscriptionLoading(false);
    }
  }

  async function handleDeleteAndReplace() {
    setDeleteLoading(true);
    setDeleteApiError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');
      const res = await fetch('/.netlify/functions/handle-ami-replace', {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ project_id: projectId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Request failed');
      if (data.action === 'proceed') {
        onChange({ ami_replace_count: data.new_replace_count ?? 1 });
        await handleDeleteData();
      }
    } catch (err) {
      setDeleteApiError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setDeleteLoading(false);
    }
  }

  async function handleDeleteAndRelock() {
    setDeleteLoading(true);
    setDeleteApiError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');
      const res = await fetch('/.netlify/functions/handle-ami-replace', {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ project_id: projectId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Request failed');
      if (data.action === 'relocked') {
        onRelockTriggered?.();
        await handleDeleteData();
      }
    } catch (err) {
      setDeleteApiError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setDeleteLoading(false);
    }
  }

  const handleDeleteData = async () => {
    await onDeleteData();
    setUploadSuccess(false);
    setUploadErrors([]);
    setUploadWarnings([]);
    setDetectedFormat('');
    setFileResults([]);
    setDeleteApiError(null);
    setShowDeleteConfirm(false);
    // Deleting or replacing AMI data clears any rehydrated (or fresh) banner.
    freshParseRef.current = false;
    setPlausibilityRehydrated(false);
    setPlausibilityFlags([]);
    setPlausibilityRowIds([]);
    setPlausibilityAction(null);

    // If this delete was initiated by a Replace-via-upload choice, parse the newly
    // selected files now that the old series is cleared. replaceExisting=true so
    // the (possibly still-stale) amiData prop is ignored. The plain Delete Data
    // button leaves this ref null, so it just clears and stops.
    const replaceFiles = replaceUploadFilesRef.current;
    if (replaceFiles && replaceFiles.length > 0) {
      replaceUploadFilesRef.current = null;
      void runProcessFiles(replaceFiles, true);
    }
  };

  function formatDate(iso: string) {
    return new Date(iso).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  /** Reading timestamps are fixed-UTC wall-clock instants; show the wall-clock date. */
  function formatUtcDate(iso: string) {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
  }

  /**
   * The link the customer was emailed, for the contractor to send himself.
   *
   * The status line has always told him to chase the customer — "a quick text
   * usually does it" — while the only copy of the link sat in the customer's
   * inbox, which is exactly the wrong place when the mail is slow or filtered.
   * Built from the current origin so staging hands out staging links.
   */
  function CopyConsentLink({ token }: { token: string }) {
    const url = `${window.location.origin}/authorize?token=${token}`;
    async function copy() {
      try {
        await navigator.clipboard.writeText(url);
      } catch {
        // Clipboard is denied in some embedded browsers; select-to-copy instead
        // of failing silently, so the link is still reachable.
        window.prompt('Copy this link and send it to your customer:', url);
        return;
      }
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    }
    return (
      <button
        type="button"
        onClick={() => void copy()}
        data-testid="btn-copy-consent-link"
        className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded border border-current text-xs font-medium hover:bg-white/60"
      >
        {copiedLink ? <Check size={13} /> : <Copy size={13} />}
        {copiedLink ? 'Link copied' : 'Copy link'}
      </button>
    );
  }

  function getConsentStatusBadge() {
    if (!consentRequest) return null;
    const isExpired =
      consentRequest.status === 'pending' &&
      new Date() > new Date(consentRequest.token_expires_at);

    // The download route has no signature step: the link is open from the
    // send until the file arrives or the window closes. Declined is the one
    // status it shares with the email route.
    // The form route: the homeowner submits the utility's own form and the
    // utility emails the file to the contractor, who loads it below.
    // The utility contact pilot shares the form route's delivered state: the
    // utility's reply loads through the same receiving address.
    const utilityPilot = consentRequest.data_route === 'utility_request';
    if (utilityPilot || (consentRequest.data_route === 'customer_request' && consentRequest.request_form_template && consentRequest.status !== 'declined')) {
      const utility = consentRequest.utility_name || 'the utility';
      if (consentRequest.utility_delivered_at) {
        const n = consentRequest.upload_reading_count ?? 0;
        const span = consentRequest.upload_first_reading && consentRequest.upload_last_reading
          ? ` from ${formatUtcDate(consentRequest.upload_first_reading)} to ${formatUtcDate(consentRequest.upload_last_reading)}`
          : '';
        return (
          <div className="flex items-start gap-2 p-3 bg-green-50 border border-green-200 rounded-md" data-testid="consent-utility-delivered">
            <span className="text-lg leading-none mt-0.5">🟢</span>
            <div className="text-sm text-green-800">
              <p>
                {utility} sent {n.toLocaleString()} readings{span} on {formatDate(consentRequest.utility_delivered_at)}.
                {amiDataCount > 0 ? ' Loaded into this project.' : ''}
              </p>
              {amiDataCount === 0 && serverReadingCount !== 0 && onReloadProject && !isReadOnly && (
                <button
                  type="button"
                  onClick={() => void onReloadProject()}
                  data-testid="btn-load-utility-delivery"
                  className="mt-2 px-3 py-1.5 rounded-md bg-green-600 text-white text-sm font-medium hover:bg-green-700"
                >
                  Load into project
                </button>
              )}
            </div>
          </div>
        );
      }
      if (utilityPilot) {
        const closed = consentRequest.receiving_expires_at ? new Date() > new Date(consentRequest.receiving_expires_at) : false;
        return (
          <div className={`flex items-start gap-2 p-3 rounded-md border ${closed ? 'bg-red-50 border-red-200' : 'bg-blue-50 border-blue-200'}`} data-testid="consent-utility-requested">
            <span className="text-lg leading-none mt-0.5">{closed ? '🔴' : '📨'}</span>
            <p className={`text-sm ${closed ? 'text-red-800' : 'text-blue-900'}`}>
              {amiDataCount > 0 ? (
                <>Requested from {utility} on {formatDate(consentRequest.created_at)}. Data loaded.</>
              ) : closed ? (
                <>Requested from {utility} ({consentRequest.utility_email}) on {formatDate(consentRequest.created_at)}; no file came back before the reply address closed. Use Get data to send the request again.</>
              ) : (
                <>
                  <strong>Requested from {utility}</strong> ({consentRequest.utility_email}) on {formatDate(consentRequest.created_at)}.{' '}
                  The reply loads here by itself, and you get a copy by email. Open until {formatDate(consentRequest.receiving_expires_at!)}.
                </>
              )}
            </p>
          </div>
        );
      }
      if (consentRequest.customer_submitted_at) {
        const expected = formatExpectedBy(expectedByDate(consentRequest.customer_submitted_at));
        return (
          <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-md" data-testid="consent-form-submitted">
            <span className="text-lg leading-none mt-0.5">📨</span>
            <p className="text-sm text-blue-900">
              {amiDataCount > 0 ? (
                <>Requested from {utility} on {formatDate(consentRequest.customer_submitted_at)}. Data loaded.</>
              ) : (
                <>
                  <strong>Requested from {utility}, expected by {expected}.</strong>{' '}
                  {consentRequest.customer_name} submitted the request form on{' '}
                  {formatDate(consentRequest.customer_submitted_at)}.{' '}
                  {consentRequest.receiving_code
                    ? <>{utility} sends the file to TrueLoad, and it loads here by itself; you get a copy by email.</>
                    : <>{utility} emails the file to you; upload it below when it arrives.</>}
                </>
              )}
            </p>
          </div>
        );
      }
      const closed = isExpired || new Date() > new Date(consentRequest.token_expires_at);
      return (
        <div className={`flex items-start gap-2 p-3 rounded-md border ${closed ? 'bg-red-50 border-red-200' : 'bg-yellow-50 border-yellow-200'}`} data-testid="consent-awaiting-form">
          <span className="text-lg leading-none mt-0.5">{closed ? '🔴' : '🟡'}</span>
          <div className={`text-sm ${closed ? 'text-red-800' : 'text-yellow-800'}`}>
            <p>
              Request link sent to <strong>{consentRequest.customer_name}</strong> on{' '}
              {formatDate(consentRequest.created_at)}.{' '}
              {consentRequest.form_opened_at ? (
                <>
                  They opened {utility}&rsquo;s form on {formatDate(consentRequest.form_opened_at)} but have not
                  confirmed they submitted it.{' '}
                  {consentRequest.receiving_code
                    ? 'If they did, the data still loads here when it arrives. '
                    : 'If they did, the file still comes to you by email. '}
                  {closed
                    ? 'The link has closed.'
                    : <>A quick text asking them to press &ldquo;I submitted the form&rdquo; on the same link gives you the expected-by date.</>}
                </>
              ) : closed
                ? `It closed without ${utility}'s form being submitted. Send a new link.`
                : <>Waiting for them to submit {utility}&rsquo;s form — the link works until {formatDate(consentRequest.token_expires_at)}.
                   Copy it below to text or email it to them yourself.</>}
            </p>
            {!closed && !isReadOnly && <CopyConsentLink token={consentRequest.token} />}
          </div>
        </div>
      );
    }
    if (isUploadRoute(consentRequest.data_route) && consentRequest.status !== 'declined') {
      if (consentRequest.homeowner_uploaded_at) {
        const n = consentRequest.upload_reading_count ?? 0;
        const span = consentRequest.upload_first_reading && consentRequest.upload_last_reading
          ? ` from ${formatUtcDate(consentRequest.upload_first_reading)} to ${formatUtcDate(consentRequest.upload_last_reading)}`
          : '';
        return (
          <div className="flex items-start gap-2 p-3 bg-green-50 border border-green-200 rounded-md" data-testid="consent-homeowner-uploaded">
            <span className="text-lg leading-none mt-0.5">🟢</span>
            <div className="text-sm text-green-800">
              <p>
                <strong>{consentRequest.customer_name}</strong> uploaded {n.toLocaleString()} readings{span} on{' '}
                {formatDate(consentRequest.homeowner_uploaded_at)}.
                {amiDataCount > 0 ? ' Loaded into this project.' : ''}
              </p>
              {amiDataCount === 0 && serverReadingCount !== 0 && onReloadProject && !isReadOnly && (
                <button
                  type="button"
                  onClick={() => void onReloadProject()}
                  data-testid="btn-load-homeowner-upload"
                  className="mt-2 px-3 py-1.5 rounded-md bg-green-600 text-white text-sm font-medium hover:bg-green-700"
                >
                  Load into project
                </button>
              )}
            </div>
          </div>
        );
      }
      const windowEnd = consentRequest.upload_expires_at ?? consentRequest.token_expires_at;
      const closed = new Date() > new Date(windowEnd);
      return (
        <div className={`flex items-start gap-2 p-3 rounded-md border ${closed ? 'bg-red-50 border-red-200' : 'bg-yellow-50 border-yellow-200'}`} data-testid="consent-awaiting-upload">
          <span className="text-lg leading-none mt-0.5">{closed ? '🔴' : '🟡'}</span>
          <div className={`text-sm ${closed ? 'text-red-800' : 'text-yellow-800'}`}>
            <p>
              {consentRequest.data_route === 'customer_request' ? 'Request link' : 'Download link'} sent to <strong>{consentRequest.customer_name}</strong> on{' '}
              {formatDate(consentRequest.created_at)}.{' '}
              {closed
                ? 'It closed with no file. Send a new link, or upload a file yourself below.'
                : <>Waiting for their file — the link works until {formatDate(windowEnd)}.
                   Copy it below to text or email it to them yourself.</>}
            </p>
            {!closed && !isReadOnly && <CopyConsentLink token={consentRequest.token} />}
          </div>
        </div>
      );
    }
    if (consentRequest.status === 'consented') {
      return (
        <div className="flex items-start gap-2 p-3 bg-green-50 border border-green-200 rounded-md">
          <span className="text-lg leading-none mt-0.5">🟢</span>
          <p className="text-sm text-green-800">
            Authorized by <strong>{consentRequest.customer_name}</strong> on{' '}
            {formatDate(consentRequest.consented_at!)}. Utility request sent.
          </p>
        </div>
      );
    }
    if (consentRequest.status === 'declined') {
      return (
        <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-md">
          <span className="text-lg leading-none mt-0.5">🔴</span>
          <p className="text-sm text-red-800">
            {isUploadRoute(consentRequest.data_route)
              ? <><strong>{consentRequest.customer_name}</strong> chose not to share their data.</>
              : 'Authorization declined by customer.'}
          </p>
        </div>
      );
    }
    if (isExpired) {
      return (
        <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-md">
          <span className="text-lg leading-none mt-0.5">🔴</span>
          <p className="text-sm text-red-800">Authorization expired. Request again.</p>
        </div>
      );
    }
    // Pending
    return (
      <div className="flex items-start gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
        <span className="text-lg leading-none mt-0.5">🟡</span>
        <div className="text-sm text-yellow-800">
          <p>
            Pending authorization from <strong>{consentRequest.customer_name}</strong>.{' '}
            Sent {formatDate(consentRequest.created_at)}. Expires{' '}
            {formatDate(consentRequest.token_expires_at)}.{' '}
            Copy the link below to send it to them yourself.
          </p>
          {!isReadOnly && <CopyConsentLink token={consentRequest.token} />}
        </div>
      </div>
    );
  }

  // Rendered first while the step is still open, and below the done state
  // once a file is attached — replacing is a detour, not the headline.
  // Rendered first while the step is still open, and below the done state once a
  // file is attached — replacing is a detour, not the headline.
  const uploadDropzone = (
    <>
      <div
        data-testid="ami-dropzone"
        className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-blue-400'
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <Upload
          className={`mx-auto mb-3 ${isDragging ? 'text-blue-500' : 'text-gray-400'}`}
          size={40}
        />
        <label className="cursor-pointer">
          <span className="text-blue-600 hover:text-blue-700 font-medium">
            Choose one or more files
          </span>
          <input
            type="file"
            accept=".csv,.txt,.tsv,.xlsx,.xls,.ods,.xml"
            multiple
            onChange={handleFileUpload}
            data-testid="ami-file-upload"
            className="hidden"
          />
        </label>
        <p className="text-sm text-gray-500 mt-2">
          or drag and drop files here — select multiple for automatic merging
        </p>
      </div>
      <div className="mt-2">
        <button
          type="button"
          onClick={() => setShowFormatsModal(true)}
          className="text-xs text-blue-600 hover:underline"
        >
          Accepted file formats
        </button>
      </div>
    </>
  );
  // The daily-peak series behind the chart, the tiles and the report figure.
  // The chart, tiles, peak-day panel and month table show the readings in
  // effect; the suspect-peak checks run on the raw readings.
  const amiInEffect = useMemo(() => readingsInEffect(amiData, project), [amiData, project]);
  const suspectWindows = useMemo(
    () => detectSuspectWindows({
      points: amiData,
      intervalMinutes: project.ami_interval_minutes ?? null,
      serviceKw: serviceDeliverableKw(project),
      estimatedRuns: project.ami_estimated_runs ?? null,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [amiData, project.ami_interval_minutes, project.service_rating_amps, project.service_voltage_numeric, project.service_phase, project.ami_estimated_runs]
  );
  const series = useMemo(
    () => (amiInEffect.length > 0 ? buildDemandSeries(amiInEffect, project.ami_interval_minutes ?? null) : null),
    [amiInEffect, project.ami_interval_minutes]
  );

  // What the quality badge and "All checks" open: the parser's own notes, then
  // every analysis line, each with the tone it deserves. Read-only — the text is
  // regenerated on every data change and prints on the report as written.
  const checklistLines = useMemo(() => {
    const lines: { tone: 'ok' | 'warn' | 'bad' | 'info'; text: string }[] = [];
    const notes = project.ami_data_quality_notes || '';
    const parserSection = notes.split('\n\n').find(sec => sec.startsWith('PARSING NOTES:'));
    if (parserSection) {
      for (const l of parserSection.split('\n').slice(1)) {
        const t = l.replace(/^- /, '').trim();
        if (t) lines.push({ tone: 'info', text: t });
      }
    }
    if (project.ami_interval_minutes === 60) {
      lines.push({
        tone: 'warn',
        text: 'Hourly readings: 15-minute peaks can run 20–40% above an hourly average, so this peak may understate short-duration demand. Disclosed on the report.',
      });
    }
    for (const issue of qualityResult?.issues ?? []) {
      const tone = issue.type === 'error' ? 'bad' : issue.type === 'warning' ? 'warn' : issue.category === 'anomaly' ? 'info' : 'ok';
      lines.push({ tone, text: issue.message });
    }
    return lines;
  }, [project.ami_data_quality_notes, project.ami_interval_minutes, qualityResult]);

  // Per-upload notices: the plausibility banner, processing notes, errors and
  // the column pickers. Rendered in the empty state and on the done card alike.
  const transientBanners = (
    <>
          {(uploadSuccess || plausibilityRehydrated) && plausibilityAction !== 'confirmed' && plausibilityFlags.some(f => f.message !== SUSPECT_PEAK_MESSAGE) && (() => {
            // Suspect-peak flags are said once, by SuspectPeakPanel, which
            // stays after this banner is confirmed away.
            const verifyFlags = plausibilityFlags.filter(f => f.severity === 'verify' && f.message !== SUSPECT_PEAK_MESSAGE);
            const infoFlags = plausibilityFlags.filter(f => f.severity === 'info');
            const needsConfirm = verifyFlags.length > 0;
            return (
              <div
                data-testid="plausibility-banner"
                className="mt-3 p-4 bg-amber-50 border border-amber-300 rounded-md"
              >
                <div className="flex items-start gap-2">
                  <AlertCircle className="text-amber-600 flex-shrink-0 mt-0.5" size={18} />
                  <div className="flex-1">
                    {needsConfirm && (
                      <p className="text-sm font-semibold text-amber-900 mb-1">
                        Please double-check this data
                      </p>
                    )}
                    {verifyFlags.length > 0 && (
                      <ul className="text-sm text-amber-800 space-y-1 list-disc pl-5">
                        {verifyFlags.map((f, i) => (
                          <li key={`v-${i}`}>{f.message}</li>
                        ))}
                      </ul>
                    )}
                    {infoFlags.length > 0 && (
                      <ul className="text-xs text-amber-700 space-y-1 mt-2 list-disc pl-5">
                        {infoFlags.map((f, i) => (
                          <li key={`i-${i}`}>{f.message}</li>
                        ))}
                      </ul>
                    )}

                    {needsConfirm && plausibilityAction === 'pending' && (
                      <div className="mt-3 flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          data-testid="plausibility-confirm"
                          onClick={handleConfirmPlausibility}
                          className="px-3 py-1.5 text-sm font-medium rounded-md bg-green-600 text-white hover:bg-green-700"
                        >
                          Confirm data looks correct
                        </button>
                        <button
                          type="button"
                          data-testid="plausibility-dispute"
                          onClick={handleDisputePlausibility}
                          className="px-3 py-1.5 text-sm font-medium rounded-md border border-amber-400 text-amber-800 hover:bg-amber-100"
                        >
                          Something looks wrong?
                        </button>
                      </div>
                    )}

                    {plausibilityAction === 'disputed' && (
                      <div className="mt-3 text-xs text-amber-800 bg-amber-100 border border-amber-200 rounded p-2 space-y-1">
                        <p className="font-medium">A few things to try:</p>
                        <ul className="list-disc pl-5 space-y-0.5">
                          {lastUpload && !isReadOnly && (
                            <li>
                              If TrueLoad read the wrong column,{' '}
                              <button
                                type="button"
                                data-testid="ami-repick-open-disputed"
                                onClick={() => setRepick(lastUpload)}
                                className="underline font-medium"
                              >
                                choose the columns yourself
                              </button>
                              . This does not count as a data replacement.
                            </li>
                          )}
                          <li>Re-check that you uploaded the correct interval-data file from the utility.</li>
                          <li>Try re-downloading the interval data export from the utility, then upload it again.</li>
                          <li>Still stuck? Contact <a href="mailto:support@trueloadapp.com" className="underline font-medium">support@trueloadapp.com</a>.</li>
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })()}

          {uploadWarnings.length > 0 && (
            <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
              <div className="flex items-start gap-2">
                <Info className="text-yellow-600 flex-shrink-0 mt-0.5" size={18} />
                <div className="flex-1">
                  <p className="text-sm font-medium text-yellow-800 mb-1">
                    Processing Notes:
                  </p>
                  <ul className="text-xs text-yellow-700 space-y-1">
                    {uploadWarnings.map((warning, idx) => (
                      <li key={idx}>{warning}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {uploadErrors.length > 0 && (
            <div data-testid="ami-upload-error" className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md">
              <div className="flex items-start gap-2">
                <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={18} />
                <div className="flex-1">
                  <p className="text-sm font-medium text-red-800 mb-1">
                    Upload Issues:
                  </p>
                  <ul className="text-xs text-red-700 space-y-1">
                    {uploadErrors.slice(0, 10).map((error, idx) => (
                      <li key={idx}>{error}</li>
                    ))}
                    {uploadErrors.length > 10 && (
                      <li>... and {uploadErrors.length - 10} more issues</li>
                    )}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {pendingPicks.length > 0 && (
            <ColumnPicker
              key={pendingPicks[0].file.name}
              fileName={pendingPicks[0].file.name}
              choices={pendingPicks[0].picker}
              reason={{ kind: 'missing', role: pendingPicks[0].picker.missing }}
              position={{ index: 1, total: pendingPicks.length }}
              busy={pickBusy}
              onConfirm={handlePickConfirm}
              onCancel={handlePickCancel}
            />
          )}

          {repick && (
            <ColumnPicker
              key={`repick-${repick.file.name}`}
              fileName={repick.file.name}
              choices={repick.choices}
              reason={{ kind: 'repick', current: repick.choices.chosen }}
              busy={pickBusy}
              onConfirm={handleRepickConfirm}
              onCancel={() => setRepick(null)}
            />
          )}
    </>
  );

  // Merge behaviour, shown only while a dropzone is up.
  const advancedOptions = (
    <>
          {/* Advanced Options — merge behaviour, so only while the dropzone is up. */}
          <div className="mt-4">
            <button
              type="button"
              onClick={() => setShowAdvanced(v => !v)}
              className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800"
            >
              <ChevronDown className={`w-4 h-4 transition-transform ${showAdvanced ? 'rotate-180' : ''}`} />
              Advanced Options
            </button>
            {showAdvanced && (
              <div className="mt-3 bg-gray-50 border border-gray-200 rounded-lg p-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Conflict Resolution Strategy
                  <Tooltip text={`If multiple files contain different values for the same timestamp, this determines which value is used. Maximum is recommended for NEC ${cite} — it is the most conservative approach.`} />
                </label>
                <select
                  value={conflictStrategy}
                  onChange={(e) => setConflictStrategy(e.target.value as ConflictStrategy)}
                  data-testid="ami-conflict-strategy"
                  className="w-full px-3 py-2.5 sm:py-2 border border-gray-300 rounded-md text-sm sm:text-base bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 touch-manipulation"
                >
                  <option value="maximum">Maximum (recommended)</option>
                  <option value="average">Average</option>
                  <option value="first">First file wins</option>
                  <option value="last">Last file wins</option>
                </select>
                <div className="mt-3 space-y-2 text-xs sm:text-sm text-gray-600">
                  <p className="font-medium text-gray-700">
                    {conflictStrategy === 'maximum' && 'Keep the higher value when conflicts occur'}
                    {conflictStrategy === 'average' && 'Use the mean of conflicting values'}
                    {conflictStrategy === 'first' && 'Earlier file takes precedence'}
                    {conflictStrategy === 'last' && 'Later file takes precedence'}
                  </p>
                  <p>
                    Applied when two files contain different demand values for the same timestamp.
                    {conflictStrategy === 'maximum' && ` Recommended for NEC ${cite} compliance.`}
                  </p>
                </div>
              </div>
            )}
          </div>
    </>
  );

  return (
    <div className="space-y-6">
      {isReadOnly && amiDataCount === 0 && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
          <p className="text-gray-600">No AMI data attached to this project</p>
        </div>
      )}

      {/* Step 1: get the interval data. Shown only while nothing is loaded — once
          a file is in, the request's outcome is a provenance line on the done
          card rather than a card of its own saying nothing was sent. */}
      {amiDataCount === 0 && projectId && !projectId.startsWith('local_') && !isReadOnly && (
        <div data-testid="ami-request-section" className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex flex-wrap items-center gap-3 mb-3">
            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-blue-600 text-white text-sm font-bold flex-shrink-0">1</span>
            <h2 className="text-xl font-semibold text-gray-900">Get the interval data</h2>
            <button
              onClick={() => {
                setRequestSuccess(null);
                setShowGetData(true);
              }}
              data-testid="btn-request-ami"
              disabled={!utilityKnown}
              className="ml-auto flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium touch-manipulation disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed disabled:hover:bg-gray-300"
            >
              <Send size={15} />
              Get data
            </button>
          </div>

          {requestSuccess && (
            <div data-testid="ami-request-sent" className="mb-3 flex items-start gap-2 p-3 bg-green-50 border border-green-200 rounded-md">
              <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={18} />
              <p className="text-sm text-green-800">
                {requestSuccess.dataRoute === 'utility_request' ? (
                  <>
                    Request sent to {utilityRow?.name || 'the utility'}. The reply loads here by itself, and
                    you get a copy by email. The reply address is open until {formatDate(requestSuccess.expiresAt)}.
                  </>
                ) : requestSuccess.dataRoute === 'customer_request' && isUsableTemplate(utilityRow?.request_form_template) ? (
                  <>
                    Request link sent. Your customer submits {utilityRow?.name || 'the utility'}&rsquo;s request
                    form from that page, and the utility emails the data to you. Upload it here when it arrives.
                    The link is open until {formatDate(requestSuccess.expiresAt)}.
                  </>
                ) : requestSuccess.dataRoute === 'customer_request' ? (
                  <>
                    Request link sent. Your customer emails the utility for their data from that
                    page, uploads the file when it arrives, and you get an email when it lands. The
                    link is open until {formatDate(requestSuccess.expiresAt)}.
                  </>
                ) : requestSuccess.dataRoute === 'self_download' ? (
                  <>
                    Download link sent. Your customer follows the steps, uploads the file on that
                    page, and you get an email when it lands. The link is open until{' '}
                    {formatDate(requestSuccess.expiresAt)}.
                  </>
                ) : (
                  <>
                    Authorization request sent. Link expires{' '}
                    {formatDate(requestSuccess.expiresAt)}.
                  </>
                )}
              </p>
            </div>
          )}

          {consentLoading && (
            <p className="text-sm text-gray-500">Loading status...</p>
          )}

          {!consentLoading && consentRequest && getConsentStatusBadge()}

          {!consentLoading && !consentRequest && !requestSuccess && (
            utilityKnown ? (
              <p className="text-sm text-gray-500" data-testid="ami-nothing-sent">
                Nothing sent yet. Get data shows how {utilityName.trim() || 'this utility'} shares interval
                data and sends your customer the link.
              </p>
            ) : (
              <p className="text-sm text-gray-500" data-testid="ami-no-utility">
                {project.utility_id && utilityLoading
                  ? 'Checking how this utility shares interval data...'
                  : 'Pick the utility on the Service Info tab first. A file you already have goes in Step 2 below.'}
              </p>
            )
          )}
        </div>
      )}

      {/* Step 2, before a file is loaded: the dropzone and the manual path. */}
      {amiDataCount === 0 && !isReadOnly && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            {projectId && !projectId.startsWith('local_') && (
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-blue-600 text-white text-sm font-bold flex-shrink-0">2</span>
            )}
            <h2 className="text-xl font-semibold text-gray-900">
              Upload AMI Data
              {showRequiredHighlights && !project.max_recorded_demand_kw && (
                <span className="ml-2 text-sm font-normal text-red-600">Required</span>
              )}
            </h2>
          </div>

          <div className="mb-4">{uploadDropzone}</div>

          {transientBanners}

          {advancedOptions}

          {!showManualEntry && (
            <div className="mt-4">
              <button
                type="button"
                onClick={() => setShowManualEntry(true)}
                className="text-sm text-blue-600 hover:underline"
              >
                No data file available? Enter demand manually.
              </button>
            </div>
          )}
          {showManualEntry && (
            <div className="mt-4 border-t border-gray-200 pt-4">
              <h3 className="text-base font-medium text-gray-900 mb-3">Manual Entry</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Max Recorded Demand (kW)
                  </label>
                  <input
                    type="number"
                    value={project.max_recorded_demand_kw || ''}
                    onChange={(e) =>
                      onChange({
                        max_recorded_demand_kw: e.target.value
                          ? Number(e.target.value)
                          : null,
                      })
                    }
                    placeholder="Enter maximum kW"
                    min="0"
                    step="0.01"
                    data-testid="ami-max-demand"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={isReadOnly}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Peak Timestamp (Optional)
                  </label>
                  <input
                    type="datetime-local"
                    value={
                      project.peak_timestamp
                        ? new Date(project.peak_timestamp)
                            .toISOString()
                            .slice(0, 16)
                        : ''
                    }
                    onChange={(e) =>
                      onChange({
                        peak_timestamp: e.target.value
                          ? new Date(e.target.value).toISOString()
                          : null,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={isReadOnly}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Done: one card. It says the step is finished, shows each fact once,
          draws the period, and keeps replacing and deleting behind one control.
          `ami-upload-success` stays on it: the harness and the demo wait for it. */}
      {amiDataCount > 0 && (
        <div data-testid="ami-upload-success" className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2.5">
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-green-600 text-white flex-shrink-0">
                <CheckCircle size={18} />
              </span>
              Interval data loaded
            </h2>
            {!isReadOnly && (
              <div className="flex flex-wrap items-center gap-2 relative" ref={manageMenuRef}>
                {onNavigate && (
                  <button
                    type="button"
                    onClick={() => onNavigate('results')}
                    data-testid="ami-continue-results"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium touch-manipulation"
                  >
                    Continue to Results
                    <ArrowRight size={16} />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setShowManageMenu(v => !v)}
                  data-testid="ami-replace-toggle"
                  aria-haspopup="menu"
                  aria-expanded={showManageMenu}
                  className="inline-flex items-center gap-1.5 px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 transition-colors touch-manipulation"
                >
                  Replace or delete
                  <ChevronDown className={`w-4 h-4 transition-transform ${showManageMenu ? 'rotate-180' : ''}`} />
                </button>
                {showManageMenu && (
                  <div
                    role="menu"
                    data-testid="ami-manage-menu"
                    className="absolute right-0 top-full mt-1.5 z-20 w-72 rounded-lg border border-gray-200 bg-white p-1.5 shadow-lg"
                  >
                    <button
                      type="button"
                      role="menuitem"
                      data-testid="ami-manage-add"
                      onClick={() => { setShowManageMenu(false); setShowReplace(true); }}
                      className="w-full text-left rounded-md px-3 py-2 hover:bg-gray-50"
                    >
                      <span className="block text-sm font-medium text-gray-900">Add or replace a file</span>
                      <span className="block text-xs text-gray-500 mt-0.5">
                        Drop the new file below. You then choose whether it adds to what is loaded
                        or replaces it.
                      </span>
                    </button>
                    <hr className="my-1.5 border-gray-100" />
                    <button
                      type="button"
                      role="menuitem"
                      data-testid="ami-manage-delete"
                      onClick={() => { setShowManageMenu(false); setShowDeleteConfirm(true); }}
                      className="w-full text-left rounded-md px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50"
                    >
                      Delete the data
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 sm:gap-6">
            <div className="flex items-start gap-3" data-testid="ami-peak-tile" data-unlocked={unlocked ? 'true' : 'false'}>
              <div className={`p-2 sm:p-3 rounded-lg flex-shrink-0 ${unlocked ? 'bg-blue-100' : 'bg-gray-100'}`}>
                {unlocked ? <Activity className="text-blue-700" size={20} /> : <Lock className="text-gray-400" size={20} />}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm text-gray-600">Peak demand</p>
                {unlocked && series ? (
                  <p data-testid="ami-peak-kw" className="text-xl sm:text-2xl font-bold text-gray-900">
                    {series.peak.kw.toFixed(2)} kW
                  </p>
                ) : (
                  <p className="text-xl sm:text-2xl font-bold text-gray-400">Locked</p>
                )}
                {series && (
                  <>
                    <p className="text-xs text-gray-500">{formatMonthDayYear(series.peak.date)}</p>
                    <p className="text-xs text-gray-500">{series.peak.timeLabel} · {series.peak.season}</p>
                  </>
                )}
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="p-2 sm:p-3 bg-gray-100 rounded-lg flex-shrink-0">
                <Database className="text-gray-600" size={20} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm text-gray-600">Data points</p>
                <p data-testid="ami-data-points" className="text-xl sm:text-2xl font-bold text-gray-900">{amiDataCount.toLocaleString()}</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="p-2 sm:p-3 bg-gray-100 rounded-lg flex-shrink-0">
                <Calendar className="text-gray-600" size={20} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm text-gray-600">Measurement period</p>
                <p data-testid="ami-summary-period" className="text-sm font-medium text-gray-900 break-words">
                  {project.ami_measurement_period || 'Not set'}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="p-2 sm:p-3 bg-gray-100 rounded-lg flex-shrink-0">
                <Clock className="text-gray-600" size={20} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm text-gray-600">Data interval</p>
                <p data-testid="ami-summary-interval" className="text-sm font-medium text-gray-900">
                  {project.ami_interval_minutes
                    ? `${project.ami_interval_minutes} minutes`
                    : 'Not detected'}
                </p>
              </div>
            </div>

            <button
              type="button"
              data-testid="ami-quality-badge"
              onClick={() => setShowChecklist(v => !v)}
              aria-expanded={showChecklist}
              title="Show the checks behind this"
              className="flex items-start gap-3 text-left rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            >
              {qualityResult && (
                <>
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                    qualityResult.tier === 'green'
                      ? 'bg-green-500'
                      : qualityResult.tier === 'yellow'
                      ? 'bg-yellow-400'
                      : 'bg-red-500'
                  }`}>
                    {qualityResult.tier === 'green' && <CheckCircle2 className="text-white" size={22} />}
                    {qualityResult.tier === 'yellow' && <AlertCircle className="text-white" size={22} />}
                    {qualityResult.tier === 'red' && <XCircle className="text-white" size={22} />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs sm:text-sm text-gray-600">Data quality</p>
                    <p className={`text-sm font-medium ${
                      qualityResult.tier === 'green'
                        ? 'text-green-700'
                        : qualityResult.tier === 'yellow'
                        ? 'text-yellow-700'
                        : 'text-red-700'
                    }`}>
                      {qualityResult.tier === 'green' && 'Data Ready'}
                      {qualityResult.tier === 'yellow' && 'Review Suggested'}
                      {qualityResult.tier === 'red' && 'Data Issues — Needs Review'}
                      <span className="ml-1 text-xs text-gray-400" aria-hidden="true">▾</span>
                    </p>
                  </div>
                </>
              )}
            </button>
          </div>

          {transientBanners}

          <SuspectPeakPanel
            project={project}
            amiData={amiData}
            windows={suspectWindows}
            unlocked={unlocked}
            isReadOnly={isReadOnly}
            onChange={onChange}
            userId={authUser?.id ?? null}
            userLabel={(userProfile?.full_name || '').trim() || authUser?.email || project.prepared_by || 'Preparer'}
          />

          {series && (
            <div className="mt-5 border-t border-gray-100 pt-4">
              <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
                <h3 className="text-base font-semibold text-gray-900">Daily peak demand</h3>
                <span className="text-xs text-gray-500">
                  Highest {series.intervalMinutes}-minute reading each day ·{' '}
                  {unlocked ? 'hover or use the arrow keys for any day' : 'values after unlock'}
                </span>
              </div>
              <DemandYearChart series={series} unlocked={unlocked} excluded={unlocked ? activeExclusions(project) : []} />
              <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-xs sm:text-sm text-gray-700" data-testid="ami-checks-line">
                <span>
                  <span className="text-green-600 font-bold">✓</span>{' '}
                  {series.days.length} days,{' '}
                  {series.seasons.length === 4
                    ? 'all four seasons'
                    : series.seasons.length === 1
                    ? `${series.seasons[0].toLowerCase()} only`
                    : `${series.seasons.length} seasons`}
                </span>
                {series.gaps.length === 0 ? (
                  <span><span className="text-green-600 font-bold">✓</span> No gaps</span>
                ) : series.gaps.length === 1 ? (
                  <span><span className="text-amber-600 font-bold">⚠</span> {series.gaps[0].minutes}-minute gap on {formatMonthDay(series.gaps[0].date)}</span>
                ) : (
                  <span><span className="text-amber-600 font-bold">⚠</span> {series.gaps.length} gaps, longest {Math.max(...series.gaps.map(g => g.minutes))} minutes</span>
                )}
                {(project.ami_merge_conflicts_resolved ?? 0) > 0 && (
                  <span><span className="text-gray-500 font-bold">ℹ</span> {(project.ami_merge_conflicts_resolved ?? 0).toLocaleString()} repeated timestamps, higher reading kept</span>
                )}
                <button
                  type="button"
                  data-testid="ami-checks-toggle"
                  onClick={() => setShowChecklist(v => !v)}
                  aria-expanded={showChecklist}
                  className="text-blue-600 underline hover:text-blue-800"
                >
                  {showChecklist ? 'Hide checks' : 'All checks'}
                </button>
              </div>
              {showChecklist && (
                <div data-testid="ami-checklist" className="mt-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm space-y-1.5">
                  {checklistLines.map((line, i) => (
                    <div key={i} className="grid grid-cols-[18px_1fr] gap-2">
                      <span className={`font-bold ${
                        line.tone === 'bad' ? 'text-red-600' : line.tone === 'warn' ? 'text-amber-600' : line.tone === 'ok' ? 'text-green-600' : 'text-gray-500'
                      }`}>
                        {line.tone === 'bad' ? '✗' : line.tone === 'warn' ? '⚠' : line.tone === 'ok' ? '✓' : 'ℹ'}
                      </span>
                      <span className="text-gray-800">{line.text}</span>
                    </div>
                  ))}
                  <p className="text-xs text-gray-500 pt-1">
                    These lines print on the report as Data Quality Notes. They are regenerated whenever the data changes.
                  </p>
                </div>
              )}
            </div>
          )}

          <div className="mt-3 text-xs text-gray-500 space-y-0.5">
            {lastUpload && !isReadOnly && (
              <p data-testid="ami-columns-used">
                Date and time from {describeChoice(lastUpload.choices, 'timestamp')}; demand from {describeChoice(lastUpload.choices, 'demand')}.{' '}
                <button
                  type="button"
                  data-testid="ami-repick-open"
                  onClick={() => setRepick(lastUpload)}
                  className="underline hover:text-gray-800"
                >
                  Not the right columns? Choose them yourself
                </button>
              </p>
            )}
            {fileResults.length > 1 && (
              <p data-testid="ami-detected-format">
                {detectedFormat}
                {' · '}
                {fileResults.map(r => `${r.name}: ${r.pointCount > 0 ? `${r.pointCount.toLocaleString()} pts` : 'no valid data'}${r.errors.length > 0 ? ' ⚠' : ''}`).join(' · ')}
              </p>
            )}
            {consentRequest?.homeowner_uploaded_at && (
              <p data-testid="consent-homeowner-uploaded">
                Uploaded by <strong>{consentRequest.customer_name}</strong> on {formatDate(consentRequest.homeowner_uploaded_at)}.
              </p>
            )}
          </div>

          {!isReadOnly && showReplace && (
            <div className="mt-4 border-t border-gray-100 pt-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-medium text-gray-800">Add or replace a file</p>
                <button
                  type="button"
                  onClick={() => setShowReplace(false)}
                  className="text-sm text-gray-500 hover:text-gray-800"
                >
                  Cancel
                </button>
              </div>
              {uploadDropzone}
              {advancedOptions}
            </div>
          )}

          {/* The file input stays in the document while the dropzone is folded:
              a second upload on a loaded project goes through this same input
              and is then intercepted by the Add-or-Replace choice. */}
          {!isReadOnly && !showReplace && (
            <input
              type="file"
              accept=".csv,.txt,.tsv,.xlsx,.xls,.ods,.xml"
              multiple
              onChange={handleFileUpload}
              data-testid="ami-file-upload"
              className="hidden"
              tabIndex={-1}
              aria-hidden="true"
            />
          )}
        </div>
      )}

      {amiDataCount > 0 && series && (
        <div className="grid grid-cols-1 lg:grid-cols-[1.15fr_1fr] gap-4 sm:gap-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6" data-testid="ami-peak-day-card">
            <h3 className="text-base font-semibold text-gray-900">Peak day · {formatLongDate(series.peak.date)}</h3>
            {unlocked ? (
              <>
                <p className="text-xs text-gray-500 mt-1 mb-3">
                  All {series.peakDay.length} readings that day. The {series.peak.timeLabel} interval is the period's maximum.
                </p>
                <PeakDayChart series={series} />
              </>
            ) : (
              <div className="mt-3 rounded-lg border border-dashed border-gray-300 px-4 py-7 text-center text-sm text-gray-500" data-testid="ami-peak-day-locked">
                <Lock className="mx-auto mb-2 text-gray-400" size={20} />
                <p>Peak-day detail appears after unlock.</p>
                <p className="text-xs mt-1">The day and time are shown above; the readings are part of the result.</p>
              </div>
            )}
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
            <h3 className="text-base font-semibold text-gray-900 mb-1">Peak by month</h3>
            <p className="text-xs text-gray-500 mb-3">The table twin of the chart, for checking a month.</p>
            <DemandMonthTable series={series} unlocked={unlocked} />
          </div>
        </div>
      )}

      {/* Notes: the one place on this tab a person writes something. */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-1">
          Notes for the AHJ
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          Printed on the report in your words. Optional.
        </p>

        <div className="space-y-4">
          {amiDataCount === 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Measurement Period
                  <Tooltip text={`The date range covered by the data. Auto-calculated when a file is uploaded. NEC ${cite} is based on 1 year of maximum demand data. Under its Exception, a continuous recording of at least 30 days is permitted${citationsFor(project).pack.exceptionBarsRenewables ? ', but not where the service has solar photovoltaic, wind, or peak load shaving' : ''}.`} />
                </label>
                <input
                  type="text"
                  value={project.ami_measurement_period || ''}
                  onChange={(e) => onChange({ ami_measurement_period: e.target.value })}
                  placeholder="e.g., Jan 1, 2025 - Dec 31, 2025"
                  data-testid="ami-period"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={isReadOnly}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data Interval (Minutes)
                  <Tooltip text="The time between readings — typically 15 minutes. Auto-detected when a file is uploaded. Shorter intervals produce more accurate peak demand characterization." />
                </label>
                <input
                  type="number"
                  value={project.ami_interval_minutes || ''}
                  onChange={(e) =>
                    onChange({ ami_interval_minutes: e.target.value ? Number(e.target.value) : null })
                  }
                  placeholder="Common: 15, 30, or 60"
                  min="1"
                  data-testid="ami-interval"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={isReadOnly}
                />
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Occupancy during the measurement period
              <Tooltip text="Was the property occupied and in typical use while the data was recorded? Note any unusual conditions such as vacancy or renovation." />
            </label>
            <input
              type="text"
              value={project.ami_occupancy_status || ''}
              onChange={(e) => onChange({ ami_occupancy_status: e.target.value })}
              placeholder="e.g., Occupied, typical use"
              data-testid="ami-occupancy"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={isReadOnly}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Anything else the AHJ should know
            </label>
            <textarea
              value={project.ami_site_notes || ''}
              onChange={(e) => onChange({ ami_site_notes: e.target.value })}
              placeholder="e.g., Detached garage on the same meter. Well pump runs on a timer overnight. House was vacant for three weeks in March."
              rows={4}
              data-testid="ami-site-notes"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={isReadOnly}
            />
          </div>
        </div>
      </div>

      {/* File Formats Modal */}
      {showFormatsModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setShowFormatsModal(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl p-6 max-w-sm w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-semibold text-gray-900">Accepted File Formats</h3>
              <button
                onClick={() => setShowFormatsModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={18} />
              </button>
            </div>
            <ul className="text-sm text-gray-700 space-y-1.5">
              <li>• CSV (.csv)</li>
              <li>• Text (.txt)</li>
              <li>• Tab-separated (.tsv)</li>
              <li>• Excel (.xlsx, .xls)</li>
              <li>• OpenDocument Spreadsheet (.ods)</li>
              <li>• Green Button XML (.xml), the standard "Download My Data" export</li>
            </ul>
            <p className="text-xs text-gray-500 mt-3">
              Multiple files can be selected and will be automatically merged. Any delimiter, headers optional.
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Apple Numbers: export to CSV first via File &gt; Export To &gt; CSV.
            </p>
          </div>
        </div>
      )}

      {pendingOutlier && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-start gap-3 mb-3">
              <AlertCircle className="text-amber-600 flex-shrink-0 mt-0.5" size={22} />
              <h3 className="text-lg font-semibold text-gray-900">
                Some readings in this file look wrong
              </h3>
            </div>

            <p className="text-sm text-gray-700 mb-3">
              {describeOutlierReview(pendingOutlier.review, unlocked)}
            </p>

            <div className="text-sm bg-gray-50 border border-gray-200 rounded-md p-3 mb-4 space-y-1">
              {unlocked ? (
                <>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Peak demand including them</span>
                    <span className="font-semibold text-gray-900" data-testid="ami-outlier-peak-with">
                      {pendingOutlier.review.maxKw.toFixed(2)} kW
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Peak demand excluding them</span>
                    <span className="font-semibold text-gray-900" data-testid="ami-outlier-peak-without">
                      {pendingOutlier.review.nextKw.toFixed(2)} kW
                    </span>
                  </div>
                </>
              ) : (
                <div className="flex justify-between">
                  <span className="text-gray-600">How far they stand above the rest</span>
                  <span className="font-semibold text-gray-900" data-testid="ami-outlier-ratio">
                    about {Math.max(1, pendingOutlier.review.cliffRatio).toFixed(0)}×
                  </span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-600">Readings affected</span>
                <span className="font-semibold text-gray-900">
                  {pendingOutlier.review.excludedTimestamps.length.toLocaleString()} of{' '}
                  {pendingOutlier.merged.length.toLocaleString()}
                </span>
              </div>
            </div>

            <p className="text-xs text-gray-500 mb-4">
              NEC {cite} uses the highest recorded demand, so this choice decides the
              calculation. Whichever you pick is recorded in the report.
            </p>

            <div className="space-y-3">
              <button
                onClick={handleOutlierExclude}
                data-testid="ami-outlier-exclude"
                className="w-full text-left p-3 border border-blue-600 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                <span className="block font-medium">Exclude these readings</span>
                <span className="block text-xs text-blue-100 mt-0.5">
                  {unlocked
                    ? `Loads the rest of the file and calculates from a peak of ${pendingOutlier.review.nextKw.toFixed(2)} kW.`
                    : 'Loads the rest of the file and calculates from the next-highest reading.'}{' '}
                  The exclusion and its reason are stated in the report.
                </span>
              </button>
              <button
                onClick={handleOutlierAccept}
                data-testid="ami-outlier-accept"
                className="w-full text-left p-3 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                <span className="block font-medium text-gray-900">Accept them as real</span>
                <span className="block text-xs text-gray-500 mt-0.5">
                  {unlocked
                    ? `Loads every reading and calculates from a peak of ${pendingOutlier.review.maxKw.toFixed(2)} kW.`
                    : 'Loads every reading, these included.'}{' '}
                  Use this if the load genuinely spiked. The review is noted in the report.
                </span>
              </button>
              <button
                onClick={handleOutlierDiscard}
                data-testid="ami-outlier-discard"
                className="w-full text-left p-3 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                <span className="block font-medium text-gray-900">Don't load this file</span>
                <span className="block text-xs text-gray-500 mt-0.5">
                  Nothing is saved. Review the file yourself, or ask the utility for a corrected
                  export, then upload again.
                </span>
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddReplaceChoice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Add to existing data, or replace it?
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              This project already has {amiDataCount.toLocaleString()} interval{amiDataCount === 1 ? '' : 's'}
              {project.ami_measurement_period ? ` (${project.ami_measurement_period})` : ''}.
            </p>
            <div className="space-y-3">
              <button
                onClick={handleAddReplaceAdd}
                data-testid="ami-choice-add"
                className="w-full text-left p-3 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                <span className="block font-medium text-gray-900">Add to existing data</span>
                <span className="block text-xs text-gray-500 mt-0.5">
                  Combines the new file with what's already here. Use this to assemble a full period from multiple partial exports.
                </span>
              </button>
              <button
                onClick={handleAddReplaceReplace}
                data-testid="ami-choice-replace"
                className="w-full text-left p-3 border border-blue-600 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                <span className="block font-medium">Replace existing data</span>
                <span className="block text-xs text-blue-100 mt-0.5">
                  Deletes the current intervals and uses only the new file. Use this if the previous data was incorrect.
                </span>
              </button>
            </div>
            <div className="flex justify-end mt-4">
              <button
                onClick={handleAddReplaceCancel}
                data-testid="ami-choice-cancel"
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirm && deleteMode === 'standard' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-red-100 rounded-full">
                <AlertCircle className="text-red-600" size={24} />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Delete AMI Data?
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  This will permanently delete all {amiDataCount.toLocaleString()} data points. This action cannot be undone.
                </p>
                <div className="flex gap-3 justify-end">
                  <button
                    onClick={() => setShowDeleteConfirm(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => void handleDeleteData()}
                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                  >
                    Delete Data
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirm && deleteMode === 'first-replace' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-amber-100 rounded-full flex-shrink-0">
                <AlertCircle className="text-amber-600" size={24} />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Replace your AMI data?
                </h3>
                <p className="text-sm text-gray-700 mb-3">
                  You can replace your AMI data once at no charge — your results will stay unlocked. Just so you know: if you need to replace it a second time, your project will re-lock and require a new unlock ($30).
                </p>
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg mb-2">
                  <p className="text-sm text-green-800">
                    Your project stays unlocked after this replacement.
                  </p>
                </div>
                <p className="text-xs text-gray-500 mb-4">
                  Working on multiple projects? The unlimited plan at $59/month covers everything with no per-project fees.
                </p>
                {deleteApiError && (
                  <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                    {deleteApiError}
                  </div>
                )}
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 justify-end">
                  <button
                    onClick={() => { setShowDeleteConfirm(false); setDeleteApiError(null); replaceUploadFilesRef.current = null; }}
                    disabled={deleteLoading || subscriptionLoading}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={startSubscriptionCheckout}
                    disabled={subscriptionLoading || deleteLoading}
                    className="px-4 py-2 text-blue-600 hover:text-blue-800 text-sm font-medium disabled:text-gray-400 transition-colors"
                  >
                    {subscriptionLoading ? 'Redirecting...' : 'Switch to Unlimited →'}
                  </button>
                  <button
                    onClick={handleDeleteAndReplace}
                    disabled={deleteLoading || subscriptionLoading}
                    className="px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {deleteLoading ? (
                      <><div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />Processing...</>
                    ) : (
                      'Replace Data — Stay Unlocked'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirm && deleteMode === 'relock' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-red-100 rounded-full flex-shrink-0">
                <AlertCircle className="text-red-600" size={24} />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  This will re-lock your project
                </h3>
                <p className="text-sm text-gray-700 mb-4">
                  Deleting this data will re-lock your project. To view results after uploading new data, you'll need to unlock again ($30) or switch to the unlimited plan ($59/month).
                </p>
                {deleteApiError && (
                  <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                    {deleteApiError}
                  </div>
                )}
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 justify-end">
                  <button
                    onClick={() => { setShowDeleteConfirm(false); setDeleteApiError(null); replaceUploadFilesRef.current = null; }}
                    disabled={deleteLoading || subscriptionLoading}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={startSubscriptionCheckout}
                    disabled={subscriptionLoading || deleteLoading}
                    className="px-4 py-2 text-blue-600 hover:text-blue-800 text-sm font-medium disabled:text-gray-400 transition-colors"
                  >
                    {subscriptionLoading ? 'Redirecting...' : 'Switch to Unlimited →'}
                  </button>
                  <button
                    onClick={handleDeleteAndRelock}
                    disabled={deleteLoading || subscriptionLoading}
                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {deleteLoading ? (
                      <><div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />Processing...</>
                    ) : (
                      'Delete & Re-Lock'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showGetData && projectId && !projectId.startsWith('local_') && (
        <GetDataPage
          projectId={projectId}
          project={project}
          utility={project.utility_id ? utilityRow : null}
          onClose={() => setShowGetData(false)}
          onSuccess={handleRequestSuccess}
        />
      )}
    </div>
  );
}
